// Custom hooks for CalculatorPage
import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { defaultFormData, emptyCompetitor, defaultConstructionCosts } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook for managing user credits
 */
export const useCredits = (initialCredits = 0) => {
  const [userCredits, setUserCredits] = useState(initialCredits);
  const [requestingCredits, setRequestingCredits] = useState(false);
  const [creditRequestPending, setCreditRequestPending] = useState(false);

  // Stable callbacks - API is a module constant, no dependencies needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCredits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/credits`, { withCredentials: true });
      setUserCredits(res.data.credits);
    } catch (error) {
      console.error("Failed to fetch credits:", error);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkCreditRequestStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/credits/requests`, { withCredentials: true });
      const pending = res.data.find(r => r.status === "pending");
      setCreditRequestPending(!!pending);
    } catch (error) {
      console.error("Failed to check credit request status:", error);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRequestCredits = useCallback(async () => {
    setRequestingCredits(true);
    try {
      await axios.post(`${API}/auth/credits/request`, { message: "Need more project credits" }, { withCredentials: true });
      toast.success("Credit request submitted! Awaiting admin approval.");
      setCreditRequestPending(true);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to request credits";
      toast.error(msg);
    } finally {
      setRequestingCredits(false);
    }
  }, []);

  return {
    userCredits,
    setUserCredits,
    requestingCredits,
    creditRequestPending,
    fetchCredits,
    checkCreditRequestStatus,
    handleRequestCredits
  };
};

/**
 * Hook for FSI configuration
 */
export const useFSIConfig = () => {
  const [constructionCosts, setConstructionCosts] = useState(defaultConstructionCosts);
  const [appConfig, setAppConfig] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchFSIConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fsi-config`, { withCredentials: true });
      if (res.data?.construction_costs) {
        // Merge with defaults so newly-added heights (e.g. "100") still work
        // even if the persisted DB config doesn't yet contain that key.
        setConstructionCosts({ ...defaultConstructionCosts, ...res.data.construction_costs });
      }
      // Cache full config so calculations get admin-configurable cost rates (TDR scrutiny, demolition, lift, C2, etc.)
      setAppConfig(res.data);
    } catch (error) {
      console.error("Failed to fetch FSI config:", error);
    }
  }, []);

  return {
    constructionCosts,
    appConfig,
    fetchFSIConfig
  };
};

/**
 * Hook for project save/load operations
 */
export const useProjectOperations = (formData, setFormData, setActiveTab) => {
  const [savedProjects, setSavedProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveProjectName, setSaveProjectName] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [lastOpenedInfo, setLastOpenedInfo] = useState(null);
  
  // Auto-save refs
  const lastSaveTimeRef = useRef(0);
  const saveTimeoutRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const formDataRef = useRef(formData);
  const currentProjectIdRef = useRef(currentProjectId);

  // Keep refs in sync
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { currentProjectIdRef.current = currentProjectId; }, [currentProjectId]);

  const fetchSavedProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await axios.get(`${API}/saved-projects`);
      setSavedProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load saved projects");
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const handleSaveProject = useCallback(async (onSuccess) => {
    const projectName = saveProjectName || formData.projectName || "Untitled Project";
    try {
      if (currentProjectId) {
        const response = await axios.put(`${API}/saved-projects/${currentProjectId}`, {
          name: projectName,
          form_data: formData
        });
        toast.success("Project saved successfully");
        if (onSuccess) onSuccess(response.data);
      } else {
        const response = await axios.post(`${API}/saved-projects`, {
          name: projectName,
          form_data: formData
        });
        setCurrentProjectId(response.data.id);
        toast.success("Project saved successfully");
        if (onSuccess) onSuccess(response.data);
      }
      setSaveDialogOpen(false);
      setSaveProjectName("");
      lastSaveTimeRef.current = Date.now();
      lastSavedDataRef.current = JSON.stringify(formData);
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Failed to save project");
    }
  }, [currentProjectId, formData, saveProjectName]);

  const handleLoadProject = useCallback(async (projectId, onLoaded) => {
    try {
      const response = await axios.get(`${API}/saved-projects/${projectId}`);
      const loadedData = response.data.form_data;

      // Backward compatibility for competitors
      if (!loadedData.competitors || !Array.isArray(loadedData.competitors)) {
        loadedData.competitors = [{ ...emptyCompetitor }];
      }
      loadedData.competitors = loadedData.competitors.map(comp => ({
        ...comp,
        bettermentChargesType: comp.bettermentChargesType || "per_member"
      }));
      if (loadedData.awardedToCompetitorIndex === undefined) {
        loadedData.awardedToCompetitorIndex = 0;
      }

      setFormData(loadedData);
      setCurrentProjectId(projectId);
      lastSaveTimeRef.current = Date.now();
      lastSavedDataRef.current = JSON.stringify(loadedData);
      
      // Set last opened info
      if (response.data.last_accessed) {
        setLastOpenedInfo({
          email: response.data.last_accessed.email,
          timestamp: response.data.last_accessed.timestamp
        });
      }
      
      setLoadDialogOpen(false);
      setActiveTab("quickinput");
      toast.success(`Loaded project: ${response.data.name}`);
      
      if (onLoaded) onLoaded(response.data);
    } catch (error) {
      console.error("Error loading project:", error);
      toast.error("Failed to load project");
    }
  }, [setFormData, setActiveTab]);

  const handleDeleteProject = useCallback(async (projectId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/saved-projects/${projectId}`);
      setSavedProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProjectId === projectId) {
        setCurrentProjectId(null);
      }
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  }, [currentProjectId]);

  const handleNewProject = useCallback(() => {
    if (window.confirm("Start a new project? Unsaved changes will be lost.")) {
      setFormData({ ...defaultFormData, competitors: [{ ...emptyCompetitor }] });
      setCurrentProjectId(null);
      setLastOpenedInfo(null);
      lastSaveTimeRef.current = 0;
      lastSavedDataRef.current = null;
      setActiveTab("quickinput");
      toast.success("New project started");
    }
  }, [setFormData, setActiveTab]);

  const handleDuplicateProject = useCallback(() => {
    const originalName = formData.societyName || formData.projectName || "Project";
    const variantName = `${originalName} - Variant`;
    setFormData(prev => ({
      ...prev,
      societyName: variantName,
      projectName: variantName
    }));
    setCurrentProjectId(null);
    lastSaveTimeRef.current = 0;
    lastSavedDataRef.current = null;
    setActiveTab("quickinput");
    toast.success(`Duplicated as "${variantName}" — change classification, FSI & free area as needed`);
  }, [formData.societyName, formData.projectName, setFormData, setActiveTab]);

  return {
    savedProjects,
    setSavedProjects,
    currentProjectId,
    setCurrentProjectId,
    saveDialogOpen,
    setSaveDialogOpen,
    loadDialogOpen,
    setLoadDialogOpen,
    saveProjectName,
    setSaveProjectName,
    loadingProjects,
    lastOpenedInfo,
    setLastOpenedInfo,
    lastSaveTimeRef,
    saveTimeoutRef,
    lastSavedDataRef,
    formDataRef,
    currentProjectIdRef,
    fetchSavedProjects,
    handleSaveProject,
    handleLoadProject,
    handleDeleteProject,
    handleNewProject,
    handleDuplicateProject
  };
};

/**
 * Hook for auto-save functionality
 */
export const useAutoSave = (formDataRef, currentProjectIdRef, lastSaveTimeRef, lastSavedDataRef) => {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);

  const autoSaveProject = useCallback(async (force = false) => {
    const fd = formDataRef.current;
    const projId = currentProjectIdRef.current;
    const projectName = fd.societyName;
    
    // Skip if no project name or already saved recently
    if (!projectName?.trim()) return;
    
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveTimeRef.current;
    
    // Don't save more often than every 60 seconds unless forced
    if (!force && timeSinceLastSave < 60000) return;
    
    // Check if data has changed
    const dataSnapshot = JSON.stringify(fd);
    if (dataSnapshot === lastSavedDataRef.current) return;
    
    setIsAutoSaving(true);
    try {
      if (projId) {
        await axios.put(`${API}/saved-projects/${projId}`, {
          name: projectName,
          form_data: fd
        });
      } else {
        const response = await axios.post(`${API}/saved-projects`, {
          name: projectName,
          form_data: fd
        });
        currentProjectIdRef.current = response.data.id;
      }
      lastSaveTimeRef.current = now;
      lastSavedDataRef.current = dataSnapshot;
      setLastAutoSaveTime(new Date());
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [formDataRef, currentProjectIdRef, lastSaveTimeRef, lastSavedDataRef]);

  // Auto-save interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      autoSaveProject();
    }, 120000); // Every 2 minutes
    return () => clearInterval(intervalId);
  }, [autoSaveProject]);

  return {
    isAutoSaving,
    lastAutoSaveTime,
    setLastAutoSaveTime,
    autoSaveProject
  };
};

/**
 * Hook for project sharing
 */
export const useProjectSharing = (currentProjectId) => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [copyEmail, setCopyEmail] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [isProjectOwner, setIsProjectOwner] = useState(true);
  const [projectOwner, setProjectOwner] = useState(null);

  const fetchSharedUsers = useCallback(async (projId) => {
    if (!projId) return;
    try {
      const res = await axios.get(`${API}/saved-projects/${projId}/shared-users`);
      setSharedUsers(res.data.shared_with || []);
      setIsProjectOwner(res.data.is_owner);
      setProjectOwner(res.data.owner);
    } catch (err) {
      console.error("Failed to fetch shared users:", err);
    }
  }, []);

  const handleShareProject = useCallback(async () => {
    if (!currentProjectId) {
      toast.error("Please save the project first before sharing");
      return;
    }
    if (!shareEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setShareLoading(true);
    try {
      const res = await axios.post(
        `${API}/saved-projects/${currentProjectId}/share`,
        { target_email: shareEmail.trim() }
      );
      toast.success(res.data.message);
      setShareEmail("");
      fetchSharedUsers(currentProjectId);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to share project";
      toast.error(msg);
    } finally {
      setShareLoading(false);
    }
  }, [currentProjectId, shareEmail, fetchSharedUsers]);

  const handleSendCopy = useCallback(async (formData) => {
    if (!currentProjectId) {
      toast.error("Please save the project first");
      return;
    }
    if (!copyEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setCopyLoading(true);
    try {
      const res = await axios.post(
        `${API}/saved-projects/${currentProjectId}/send-copy`,
        { target_email: copyEmail.trim(), form_data: formData }
      );
      toast.success(res.data.message);
      setCopyEmail("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send copy";
      toast.error(msg);
    } finally {
      setCopyLoading(false);
    }
  }, [currentProjectId, copyEmail]);

  const handleRemoveSharedUser = useCallback(async (email) => {
    if (!currentProjectId) return;
    try {
      await axios.delete(`${API}/saved-projects/${currentProjectId}/share/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email} from shared users`);
      fetchSharedUsers(currentProjectId);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to remove user";
      toast.error(msg);
    }
  }, [currentProjectId, fetchSharedUsers]);

  return {
    shareDialogOpen,
    setShareDialogOpen,
    shareEmail,
    setShareEmail,
    copyEmail,
    setCopyEmail,
    shareLoading,
    copyLoading,
    sharedUsers,
    isProjectOwner,
    setIsProjectOwner,
    projectOwner,
    fetchSharedUsers,
    handleShareProject,
    handleSendCopy,
    handleRemoveSharedUser
  };
};

/**
 * Hook for export/import operations
 */
export const useExportImport = (formData, setFormData, setCurrentProjectId, setActiveTab) => {
  const handleExportProject = useCallback(async () => {
    let version = "1.9.0";
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/app-version`);
      version = res.data.version;
    } catch (err) {
      console.error("Failed to fetch app version for export:", err);
    }
    const projectData = {
      name: formData.projectName || formData.societyName || "Untitled",
      form_data: formData,
      app_version: version,
      exported_at: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(projectData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${projectData.name.replace(/[^a-zA-Z0-9]/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Project exported successfully");
  }, [formData]);

  const handleImportProject = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        const imported = data.form_data || data;
        if (!imported || typeof imported !== "object") {
          toast.error("Invalid project file");
          return;
        }
        setFormData(imported);
        setCurrentProjectId(null);
        setActiveTab("quickinput");
        toast.success(`Project "${data.name || "Imported"}" loaded successfully`);
      } catch {
        toast.error("Failed to read project file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }, [setFormData, setCurrentProjectId, setActiveTab]);

  return {
    handleExportProject,
    handleImportProject
  };
};
